import { BackgroundConfig } from "../../helpers/backgroundHelper";
import { PostProcessingConfig } from "../../helpers/postProcessingHelper";
import { inputResolutions, SegmentationConfig } from "../../helpers/segmentationHelper";
import { SourcePlayback } from "../../helpers/sourceHelper";
import { TFLite } from "../../hooks/useTFLite";
import { TimerWorker } from "../../helpers/timerHelper";
import { compileShader, createTexture, glsl } from "../helpers/webglHelper";
import { BackgroundBlurStage, buildBackgroundBlurStage } from "./backgroundBlurStage";
import { BackgroundImageStage, buildBackgroundImageStage } from "./backgroundImageStage";
import { buildJointBilateralFilterStage } from "./jointBilateralFilterStage";
import { buildLoadSegmentationStage } from "./loadSegmentationStage";
import { buildResizingStage } from "./resizingStage";
import { buildSoftmaxStage } from "./softmaxStage";
import { RenderingPipeline } from "../../helpers/renderingPipelineHelper";
import { InferenceWorker } from "../../hooks/useInferenceWorker";
import type { SegmenterHandle } from "../../hooks/useImageSegmenter";

export function buildWebGL2Pipeline(
  sourcePlayback: SourcePlayback,
  backgroundImage: HTMLImageElement | null,
  backgroundConfig: BackgroundConfig,
  segmentationConfig: SegmentationConfig,
  canvas: HTMLCanvasElement,
  tflite: TFLite,
  timerWorker: TimerWorker,
  addFrameEvent: () => void,
  // When set, inference runs off the main thread in this worker; otherwise tflite runs inline.
  inferenceWorker: InferenceWorker | null = null,
  // SPIKE: when set, MediaPipe ImageSegmenter (GPU delegate) produces the mask, replacing tflite entirely.
  imageSegmenter: SegmenterHandle | null = null,
): RenderingPipeline {
  const vertexShaderSource = glsl`#version 300 es

    in vec2 a_position;
    in vec2 a_texCoord;

    out vec2 v_texCoord;

    void main() {
      gl_Position = vec4(a_position, 0.0, 1.0);
      v_texCoord = a_texCoord;
    }
  `;

  const { width: frameWidth, height: frameHeight } = sourcePlayback;
  // MediaPipe returns the mask at the input (video) resolution, not the tflite input enum — size the
  // segmentation textures to the frame so the mask uploads with the correct stride.
  const [segmentationWidth, segmentationHeight] = imageSegmenter
    ? [frameWidth, frameHeight]
    : inputResolutions[segmentationConfig.inputResolution];

  const gl = canvas.getContext("webgl2")!;

  const vertexShader = compileShader(gl, gl.VERTEX_SHADER, vertexShaderSource);

  const vertexArray = gl.createVertexArray();
  gl.bindVertexArray(vertexArray);

  const positionBuffer = gl.createBuffer()!;
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1.0, -1.0, 1.0, -1.0, -1.0, 1.0, 1.0, 1.0]), gl.STATIC_DRAW);

  const texCoordBuffer = gl.createBuffer()!;
  gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 1.0, 1.0]), gl.STATIC_DRAW);

  // We don't use texStorage2D here because texImage2D seems faster
  // to upload video texture than texSubImage2D even though the latter
  // is supposed to be the recommended way:
  // https://developer.mozilla.org/en-US/docs/Web/API/WebGL_API/WebGL_best_practices#use_texstorage_to_create_textures
  const inputFrameTexture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, inputFrameTexture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);

  // TODO Rename segmentation and person mask to be more specific
  const segmentationTexture = createTexture(gl, gl.RGBA8, segmentationWidth, segmentationHeight)!;
  const personMaskTexture = createTexture(gl, gl.RGBA8, frameWidth, frameHeight)!;

  // MediaPipe produces the mask directly from the video frame, so the tflite resize stage is unused.
  const resizingStage = imageSegmenter
    ? null
    : buildResizingStage(timerWorker, gl, vertexShader, positionBuffer, texCoordBuffer, segmentationConfig, tflite, !!inferenceWorker);

  // Mask seam: the segmentation stage reads its mask from whichever backend is active instead of tflite's HEAP.
  // Until the first async mask arrives, feed a zero mask so texSubImage2D always has valid data.
  // MediaPipe and mlkit are single-channel (R); meet's tflite output is two-channel (RG, softmaxed on GPU).
  const useMediaPipe = !!imageSegmenter;
  const segmentationChannels = !useMediaPipe && segmentationConfig.model === "meet" ? 2 : 1;
  const zeroMask = new Float32Array(segmentationWidth * segmentationHeight * segmentationChannels);
  const maskAccessor = imageSegmenter
    ? () => imageSegmenter.getLatestMask() ?? { data: zeroMask, offset: 0 }
    : inferenceWorker
    ? () => inferenceWorker.getLatestMask() ?? { data: zeroMask, offset: 0 }
    : undefined;

  // Softmax is only for meet's two-channel tflite output; MediaPipe/mlkit go through the single-channel loader.
  const loadSegmentationStage =
    !useMediaPipe && segmentationConfig.model === "meet"
      ? buildSoftmaxStage(
          gl,
          vertexShader,
          positionBuffer,
          texCoordBuffer,
          segmentationConfig,
          tflite,
          segmentationTexture,
          maskAccessor,
        )
      : buildLoadSegmentationStage(
          gl,
          vertexShader,
          positionBuffer,
          texCoordBuffer,
          segmentationConfig,
          tflite,
          segmentationTexture,
          maskAccessor,
          [segmentationWidth, segmentationHeight],
        );
  const jointBilateralFilterStage = buildJointBilateralFilterStage(
    gl,
    vertexShader,
    positionBuffer,
    texCoordBuffer,
    segmentationTexture,
    segmentationConfig,
    personMaskTexture,
    canvas,
    [segmentationWidth, segmentationHeight],
  );


  
  const backgroundStage =
    backgroundConfig.type === "blur"
      ? buildBackgroundBlurStage(
          gl,
          vertexShader,
          positionBuffer,
          texCoordBuffer,
          personMaskTexture,
          canvas,
          backgroundConfig.scale,
        )
      : buildBackgroundImageStage(gl, positionBuffer, texCoordBuffer, personMaskTexture, backgroundImage, canvas);

  async function render() {
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, inputFrameTexture);

    // texImage2D seems faster than texSubImage2D to upload
    // video texture
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, sourcePlayback.htmlElement);

    gl.bindVertexArray(vertexArray);

    if (imageSegmenter) {
      // MediaPipe consumes the video element directly (its own resize + GPU inference); no tflite path.
      // htmlElement is always the camera <video> in this pipeline (createSourcePlayback builds a video).
      imageSegmenter.segment(sourcePlayback.htmlElement as HTMLVideoElement, performance.now());
      addFrameEvent();
      addFrameEvent();
    } else {
      await resizingStage!.render();

      addFrameEvent();

      if (inferenceWorker) {
        // Post the resized frame to the worker; this frame renders with the most recent mask available
        // (~1 extra frame of latency vs inline, imperceptible at call frame rates).
        inferenceWorker.infer(resizingStage!.outputPixels, segmentationWidth, segmentationHeight);
      } else {
        tflite._runInference();
      }

      addFrameEvent();
    }

    loadSegmentationStage.render();
    jointBilateralFilterStage.render();
    backgroundStage.render();
  }

  function updatePostProcessingConfig(postProcessingConfig: PostProcessingConfig) {
    jointBilateralFilterStage.updateSigmaSpace(postProcessingConfig.jointBilateralFilter.sigmaSpace);
    jointBilateralFilterStage.updateSigmaColor(postProcessingConfig.jointBilateralFilter.sigmaColor);

    if (backgroundConfig.type === "image") {
      const backgroundImageStage = backgroundStage as BackgroundImageStage;
      backgroundImageStage.updateCoverage(postProcessingConfig.coverage);
      backgroundImageStage.updateLightWrapping(postProcessingConfig.lightWrapping);
      backgroundImageStage.updateBlendMode(postProcessingConfig.blendMode);
    } else if (backgroundConfig.type === "blur") {
      const backgroundBlurStage = backgroundStage as BackgroundBlurStage;
      backgroundBlurStage.updateCoverage(postProcessingConfig.coverage);
    } else {
      // TODO Handle no background in a separate pipeline path
      const backgroundImageStage = backgroundStage as BackgroundImageStage;
      backgroundImageStage.updateCoverage([0, 0.9999]);
      backgroundImageStage.updateLightWrapping(0);
    }
  }

  let cleanedUp = false;

  function cleanUp() {
    if (cleanedUp) {
      return;
    }

    cleanedUp = true;

    imageSegmenter?.terminate();
    inferenceWorker?.terminate();
    backgroundStage.cleanUp();
    jointBilateralFilterStage.cleanUp();
    loadSegmentationStage.cleanUp();
    resizingStage?.cleanUp();

    gl.deleteTexture(personMaskTexture);
    gl.deleteTexture(segmentationTexture);
    gl.deleteTexture(inputFrameTexture);
    gl.deleteBuffer(texCoordBuffer);
    gl.deleteBuffer(positionBuffer);
    gl.deleteVertexArray(vertexArray);
    gl.deleteShader(vertexShader);
  }

  return { render, updatePostProcessingConfig, cleanUp, cleanedUp };
}
